import { useTranslation, TFunction } from 'react-i18next';

import {
  DatePicker,
  DateTimePicker,
  DateTools,
  FilterItem,
  Icon,
  Select,
  TDatePickerProps,
  TOption,
} from '@treezor/design-system';

import { useCallback } from 'react';

import { RenderSelectedValues } from './RenderSelectedValues';
import { IPeriod, IPeriodField, periodBases } from './filters.type';

export interface IPeriodFilterProps {
  field: IPeriodField;
  label: string;
  selectBase?: TOption[];
  dateformat?: string;
  canSelectTime?: boolean;
}

export function parseBasesPeriod(t: TFunction<'translation', undefined>, bases = periodBases) {
  return Object.keys(bases).map((base) => ({
    label: t(`components.molecules.periodBases.${base}`),
    value: base,
  }));
}

export function isFrom(value: IPeriod) {
  return (value?.base === periodBases.ON || value.base === periodBases.AFTER) && !!value.from;
}

export function isTo(value: IPeriod) {
  return value.base === periodBases.BEFORE && !!value.to;
}

export function isBetween(value: IPeriod) {
  return value.base === periodBases.BETWEEN && !!value.from && !!value.to;
}

export function PeriodFilter({
  label,
  field,
  selectBase,
  dateformat = 'api-date-time',
  canSelectTime = false,
}: IPeriodFilterProps) {
  const { t } = useTranslation();

  const handleBaseChange = useCallback(
    (value) => {
      field?.handleChange({ base: value, from: null, to: null });
    },
    [field],
  );

  const handleFromChange = useCallback(
    (value) => {
      field?.handleChange({ base: null, from: DateTools.format(value, dateformat), to: null });
    },
    [field, dateformat],
  );

  const handleToChange = useCallback(
    (value) => {
      field?.handleChange({ base: null, from: null, to: DateTools.format(value, dateformat) });
    },
    [field, dateformat],
  );

  const handleResetChange = useCallback(() => {
    field?.handleChange({ base: null, from: null, to: null });
  }, [field]);

  const commonDatePickerProps: Pick<
    TDatePickerProps,
    'size' | 'todayButtonLabel' | 'clearButtonLabel' | 'placeholder'
  > = {
    size: 's',
    todayButtonLabel: t('components.atoms.datefield.today'),
    clearButtonLabel: t('components.atoms.datefield.clear'),
    placeholder: 'DD/MM/YYYY',
  };

  let valuesLabel = undefined;

  if (!!field.value && isFrom(field.value)) {
    valuesLabel = [
      `${t(`components.molecules.periodBases.${field.value.base}`)} ${DateTools.format(field.value.from)}`,
    ];
  } else if (!!field.value && isTo(field.value)) {
    valuesLabel = [`${t(`components.molecules.periodBases.${field.value.base}`)} ${DateTools.format(field.value.to)}`];
  } else if (!!field.value && isBetween(field.value)) {
    valuesLabel = [
      `${t(`components.molecules.periodBases.${field.value.base}`)} ${DateTools.format(field.value.from)} - ${DateTools.format(field.value.to)}`,
    ];
  }

  const PeriodPicker = canSelectTime ? DateTimePicker : DatePicker;

  return (
    <FilterItem
      type="CUSTOM"
      id="status"
      renderSelectedValues={(closeDropDown) => (
        <RenderSelectedValues
          label={label}
          values={valuesLabel}
          onReset={() => {
            handleResetChange();
            closeDropDown();
          }}
        />
      )}
      dropdownContent={(closeDropDown) => (
        <div className="flex flex-col py-3 px-4">
          <div className="p-3">
            <span className="typo-subtitle-subtitle3">{t('components.atoms.datefield.filterByDate')}</span>
          </div>
          <div className="flex items-center justify-start gap-[10px]">
            {!!selectBase && (
              <Select
                options={selectBase}
                defaultValue={field.value?.base || selectBase[0].value}
                size="s"
                onChange={(value) => {
                  handleBaseChange(value);
                }}
              />
            )}
            {field.value?.base !== periodBases.BEFORE && (
              <PeriodPicker
                selectedDate={!!field.value?.from && new Date(field.value?.from)}
                onChangeDate={(value) => {
                  if (canSelectTime) {
                    // Not trigger onChange function upon opening the DateTimePicker
                    if (DateTools.compare(field.value?.from, value) === 0) return;
                  } else {
                    if (field.value?.base === periodBases.AFTER || field.value?.base === periodBases.ON) {
                      closeDropDown();
                    }
                  }

                  handleFromChange(value);
                }}
                maxDate={!!field.value?.to ? new Date(field.value?.to) : new Date()}
                {...commonDatePickerProps}
              />
            )}
            {(field.value?.base === periodBases.BEFORE || field.value?.base === periodBases.BETWEEN) && (
              <>
                <span>
                  <Icon name="arrow_right_alt" size="s" />
                </span>
                <PeriodPicker
                  selectedDate={!!field.value?.to && new Date(field.value?.to)}
                  onChangeDate={(value) => {
                    if (canSelectTime) {
                      // Not trigger onChange function upon opening the DateTimePicker
                      if (DateTools.compare(field.value?.to, value) === 0) return;
                    } else {
                      if (field.value?.base === periodBases.BEFORE && !canSelectTime) {
                        closeDropDown();
                      }
                    }

                    handleToChange(value);
                  }}
                  minDate={!!field.value?.from ? new Date(field.value.from) : undefined}
                  maxDate={new Date()}
                  {...commonDatePickerProps}
                />
              </>
            )}
          </div>
        </div>
      )}
    />
  );
}
